import { ButcherHeader } from '@components/ButcherHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faEllipsisVertical,
  faSearch,
  faSort,
  faSortUp,
  faSortDown,
} from '@fortawesome/free-solid-svg-icons';
import { SideNav } from '@components/OrderCreation/OrderSideNav';
import Pagination from '@components/Pagination';
import { useEffect, useState, useRef } from 'react';
import {
  errorMessage,
  InvocieCrumbSteps,
  OrderProcessingMasterData,
  invoiceStatusLabels,
  invoiceStatuses,
  pagination,
  searchConfig,
} from '@helpers/Constants';
import { ApiEndpoints, FrontendRoutes } from '@helpers/Constants/Navigation';
import { Axios } from '@helpers/ApiService';
import { useToast } from '@contexts/Toast';
import { OrderStatusBadge } from '@components/OrderStatusBadge';
import { InvoiceListProps } from '@helpers/Models';
import { OrderListBreadcrumbs } from '@components/OrderProcessing/OrderListBreadcrumbs';
import { formatDate } from '@services/OrderCreationService';
import { useNavigate } from 'react-router-dom';
import { useDismissDropdown } from '@services/DropdownService';

export const InvoiceList: React.FC = () => {
  const navigate = useNavigate();
  const { showToastMessage } = useToast();
  const [currentPage, setCurrentPage] = useState(pagination.defaultPage);
  const [itemsPerPage, setItemsPerPage] = useState(pagination.itemsPerPage);
  const [totalPages, setTotalPages] = useState(pagination.defaultPage);
  const [searchKey, setSearchKey] = useState('');
  const [allInvoice, setAllInvoice] = useState<InvoiceListProps[]>([]);
  const [debouncedSearchKey, setDebouncedSearchKey] = useState('');
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [activeDropdown, setActiveDropdown] = useState<number | null>(null);
  const dropdownRefs = useRef<Map<number, HTMLDivElement>>(new Map());
  const filterDropdownRef = useRef<HTMLDivElement | null>(null);
  const [selectedOrderStatus, setSelectedOrderStatus] = useState<number | null>(
    null,
  );
  const [isFilterDropdownOpen, setIsFilterDropdownOpen] = useState(false);

  // ---------- Sorting state ----------
  const [sortField, setSortField] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const selectedOrderStatusLabel =
    (selectedOrderStatus !== null ? invoiceStatusLabels[selectedOrderStatus] : undefined) ??
    'All Invoices';

  // Debounce search
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchKey(searchKey);
    }, searchConfig.SEARCH_DELAY);

    return () => {
      clearTimeout(handler);
    };
  }, [searchKey]);

  // Fetch data when any filter, pagination, or sort changes
  useEffect(() => {
    fetchAllInvoice(
      currentPage,
      debouncedSearchKey,
      itemsPerPage,
      selectedOrderStatus,
      sortField,
      sortDirection,
    );
  }, [currentPage, itemsPerPage, debouncedSearchKey, selectedOrderStatus, sortField, sortDirection]);

  // Close dropdown on outside click
  useDismissDropdown({
    refs: [
      ...Array.from(dropdownRefs.current.values()).map((el) => ({
        current: el,
      })),
      { current: filterDropdownRef.current },
    ],
    onClose: () => {
      setActiveDropdown(null);
      setIsFilterDropdownOpen(false);
    },
  });

  /**
   * Fetches a paginated list of invoices from the API.
   */
  const fetchAllInvoice = async (
    page: number,
    searchKeyVal: string,
    count: number = itemsPerPage,
    status: number | null = selectedOrderStatus,
    field: string = sortField,
    direction: 'asc' | 'desc' = sortDirection,
  ): Promise<void> => {
    try {
      const payload: {
        page: number;
        count: number;
        searchKey: string;
        status?: number;
        sort?: Record<string, 'asc' | 'desc'>;
      } = {
        page,
        count,
        searchKey: searchKeyVal,
      };

      if (status !== null) {
        payload.status = status;
      }

      // FIX: Use the field name as-is (no mapping) so that 'invoiceDate' is sent correctly.
      if (field) {
        payload.sort = { [field]: direction };
      }

      const response = await Axios.post(ApiEndpoints.INVOICE_LIST, payload);
      const data = response?.data?.data;
      const rawOrders: InvoiceListProps[] = data?.orders ?? [];

      // Deduplicate orders returned by API to prevent duplicate keys and DOM persistence issues
      const uniqueOrdersMap = new Map<number, InvoiceListProps>();
      rawOrders.forEach((order) => {
        if (order && order.id != null) {
          if (!uniqueOrdersMap.has(order.id)) {
            uniqueOrdersMap.set(order.id, order);
          }
        }
      });

      const orders = uniqueOrdersMap.size > 0 ? Array.from(uniqueOrdersMap.values()) : rawOrders;

      setAllInvoice(orders);
      setCurrentPage(data?.currentPage ?? 1);
      setTotalPages(data?.totalPages ?? 1);
    } catch (error) {
      showToastMessage(errorMessage.FETCH_ERROR, 'error');
    }
  };

  const toggleDropdown = (orderId: number) => {
    setActiveDropdown(activeDropdown === orderId ? null : orderId);
  };

  const handleStatusFilterSelect = (status: number | null): void => {
    setSelectedOrderStatus(status);
    setCurrentPage(1); // Reset to first page when filter changes
    setIsFilterDropdownOpen(false);
  };

  // Navigation helpers
  const handleViewOrderSummary = (orderId: number) => {
    navigate(FrontendRoutes.ORDER_DETAILS, { state: { orderId } });
  };

  const handleViewInvoiceDetails = (invoice: InvoiceListProps) => {
    navigate(FrontendRoutes.INVOICE_DETAILS, {
      state: {
        invoiceId: invoice.id,
        orderNumber: invoice.orderNumber,
        customerName: invoice.customerName,
        roles: invoice.roles ?? '',
        createdBy: invoice.createdUserName ?? '',
        createdDate: invoice.createdAt ?? invoice.invoiceDate,
        fromPickup: false,
      },
    });
  };

  const handleViewInvoices = (orderId: number) => {
    navigate(FrontendRoutes.INVOICE_ORDERED_ITEMS, { state: { orderId } });
  };

  const handleViewServiceProcessing = (orderId: number) => {
    navigate(FrontendRoutes.SERVICE_PROCESS_TASK_LIST, { state: { orderId } });
  };

  const handleViewProductProcessing = (orderId: number) => {
    navigate(FrontendRoutes.PRODUCT_PROCESSING_TASK_LIST, { state: { orderId } });
  };

  /**
   * Handles column header click to set sort field and direction.
   */
  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  /**
   * Renders the sort icon for a given column.
   */
  const renderSortIcon = (field: string) => {
    if (sortField !== field) {
      return <FontAwesomeIcon icon={faSort} className="ml-1 text-gray-300" />;
    }
    if (sortDirection === 'asc') {
      return <FontAwesomeIcon icon={faSortUp} className="ml-1 text-gray-600" />;
    }
    return <FontAwesomeIcon icon={faSortDown} className="ml-1 text-gray-600" />;
  };

  const BreadCrumbsProps = {
    steps: InvocieCrumbSteps,
    activeItem: 'Invoice',
    type: OrderProcessingMasterData.INVOICE,
  };

  return (
    <div className="flex h-screen w-screen gap-1">
      <SideNav active={6} />
      <div className="flex-1 p-1 overflow-auto relative">
        <ButcherHeader />
        <OrderListBreadcrumbs orderInfo={BreadCrumbsProps} />
        <div className="flex justify-between items-center p-3">
          <h1 className="font-semibold text-xl px-5 mt-3">Invoice</h1>
          <div className="flex items-center gap-2">
            <div className="relative" ref={filterDropdownRef}>
              <button
                type="button"
                onClick={() =>
                  setIsFilterDropdownOpen((previousState) => !previousState)
                }
                className="min-w-[180px] h-10 border border-gray-300 bg-white px-4 rounded text-left text-sm font-medium text-[#1C1B1F] flex items-center justify-between gap-3 hover:border-primary-green focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-green"
              >
                <span>{selectedOrderStatusLabel}</span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`text-gray-500 transition-transform duration-200 ${isFilterDropdownOpen ? 'rotate-180' : ''}`}
                />
              </button>
              {isFilterDropdownOpen && (
                <div className="absolute left-0 mt-2 w-56 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                  <ul className="py-2">
                    <li className="mx-2 my-0">
                      <button
                        type="button"
                        onClick={() => handleStatusFilterSelect(null)}
                        className="w-full rounded-md px-4 py-2 text-left text-sm font-semibold text-[#1C1B1F] hover:bg-primary hover:text-white"
                      >
                        All Invoices
                      </button>
                    </li>
                    {Object.values(invoiceStatuses).map((statusValue) => (
                      <li key={statusValue} className="mx-2 my-0">
                        <button
                          type="button"
                          onClick={() => handleStatusFilterSelect(statusValue)}
                          className="w-full rounded-md px-4 py-2 text-left text-sm font-semibold text-[#1C1B1F] hover:bg-primary hover:text-white"
                        >
                          {invoiceStatusLabels[statusValue]}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="relative">
              <input
                type="text"
                placeholder="Search..."
                onChange={(e) => setSearchKey(e.target.value)}
                value={searchKey}
                className="border p-2 pl-3 pr-10 w-80 accent-primary-green focus:outline-primary-green border-gray-300 rounded"
              />
              <FontAwesomeIcon
                icon={faSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
              />
            </div>
          </div>
        </div>
        <div className="p-3">
          <div className="shadow-lg border border-gray-300 rounded-lg">
            <form onSubmit={(e) => e.preventDefault()}>
              <table className="w-full table-auto rounded-xl">
                <thead className="bg-gray-100">
                  <tr className="border-b">
                    <th className="px-4 py-3 text-center font-semibold align-middle">
                      <button
                        type="button"
                        onClick={() => handleSort('invoiceNumber')}
                        className="flex items-center justify-center gap-1 w-full focus:outline-none"
                      >
                        Invoice No.
                        {renderSortIcon('invoiceNumber')}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-center font-semibold align-middle">
                      <button
                        type="button"
                        onClick={() => handleSort('orderNumber')}
                        className="flex items-center justify-center gap-1 w-full focus:outline-none"
                      >
                        Order No.
                        {renderSortIcon('orderNumber')}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-center font-semibold align-middle">
                      <button
                        type="button"
                        onClick={() => handleSort('customerName')}
                        className="flex items-center justify-center gap-1 w-full focus:outline-none"
                      >
                        Customer
                        {renderSortIcon('customerName')}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-center font-semibold align-middle">
                      <button
                        type="button"
                        onClick={() => handleSort('speciesName')}
                        className="flex items-center justify-center gap-1 w-full focus:outline-none"
                      >
                        Species
                        {renderSortIcon('speciesName')}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-center font-semibold align-middle">
                      <button
                        type="button"
                        onClick={() => handleSort('invoiceDate')}
                        className="flex items-center justify-center gap-1 w-full focus:outline-none"
                      >
                        Invoice Date
                        {renderSortIcon('invoiceDate')}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-center font-semibold align-middle">
                      <button
                        type="button"
                        onClick={() => handleSort('status')}
                        className="flex items-center justify-center gap-1 w-full focus:outline-none"
                      >
                        Invoice Status
                        {renderSortIcon('status')}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-center font-semibold align-middle">
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {allInvoice.length > 0 ? (
                    allInvoice.map((invoice, index) => (
                      <tr
                        onClick={() => {
                          handleViewInvoiceDetails(invoice);
                          setSelectedOrderId(invoice.id);
                        }}
                        key={`invoice-${invoice.id}-${index}`}
                        className={`hover:cursor-pointer hover:bg-tertiary hover:border hover:border-primary hover:b-2 ${selectedOrderId === invoice.id
                          ? 'bg-tertiary border border-primary'
                          : ''
                          }`}
                      >
                        <td className="px-4 py-3 text-center whitespace-nowrap align-middle">
                          {invoice.invoiceNumber}
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap align-middle">
                          {invoice.orderNumber}
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap align-middle">
                          {invoice.customerName}
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap align-middle">
                          {invoice.speciesName || '-'}
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap align-middle">
                          {formatDate(String(invoice.invoiceDate || invoice.createdAt || ''))}
                        </td>
                        <td className="px-4 py-3 text-center whitespace-nowrap align-middle">
                          <div className="flex justify-center items-center">
                            <OrderStatusBadge
                              type="invoiceStatus"
                              status={invoice.status}
                            />
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center relative align-middle">
                          <button
                            type="button"
                            className="p-3"
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleDropdown(invoice.id);
                            }}
                          >
                            <FontAwesomeIcon icon={faEllipsisVertical} />
                          </button>

                          {activeDropdown === invoice.id && (
                            <div
                              className="absolute right-0 mt-2 w-56 bg-white border rounded-lg shadow-lg z-10"
                              ref={(el) => {
                                if (el) {
                                  dropdownRefs.current.set(invoice.id, el);
                                } else {
                                  dropdownRefs.current.delete(invoice.id);
                                }
                              }}
                            >
                              <ul className="py-2 text-left">
                                <li className="mx-2 my-0">
                                  <button
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleViewOrderSummary(invoice.id);
                                    }}
                                    type="button"
                                    className="w-full px-4 py-2 text-sm font-semibold hover:bg-[#EFF1F5] cursor-pointer rounded-md text-left"
                                  >
                                    View order summary
                                  </button>
                                </li>
                                <li className="mx-2 my-0">
                                  <button
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleViewInvoices(invoice.id);
                                    }}
                                    type="button"
                                    className="w-full px-4 py-2 text-sm font-semibold hover:bg-[#EFF1F5] cursor-pointer rounded-md text-left"
                                  >
                                    View invoices
                                  </button>
                                </li>
                                <li className="mx-2 my-0">
                                  <button
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleViewServiceProcessing(invoice.id);
                                    }}
                                    type="button"
                                    className="w-full px-4 py-2 text-sm font-semibold hover:bg-[#EFF1F5] cursor-pointer rounded-md text-left"
                                  >
                                    View service processing
                                  </button>
                                </li>
                                <li className="mx-2 my-0">
                                  <button
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      handleViewProductProcessing(invoice.id);
                                    }}
                                    type="button"
                                    className="w-full px-4 py-2 text-sm font-semibold hover:bg-[#EFF1F5] cursor-pointer rounded-md text-left"
                                  >
                                    View product processing
                                  </button>
                                </li>
                              </ul>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={7} className="px-4 py-3 text-center align-middle">
                        No data available.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </form>

            <div className="bottom-0">
              <Pagination
                totalPages={totalPages}
                currentPage={currentPage}
                itemsPerPage={itemsPerPage}
                onPageChange={setCurrentPage}
                onItemsPerPageChange={setItemsPerPage}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};